import React, { useState, useEffect } from "react";
import {
    CONTRACT,
    MAX_INT_VALUE,
    DEFAULT_FETCHED_OPTIONS,
    DEFAULT_PAGE,
} from "consts";
import { getUsers, getUsersWithContracts } from "api";
//
import {
    Button,
    Grid,
    Checkbox,
    FormGroup,
    FormControl,
    FormControlLabel
} from "@material-ui/core";
//
import { UserAutoSuggest } from "containers/components/autoSuggest";

const UserPicker = (props) => {
    const [users, setUsers] = useState([]);
    const { contractType, userIds, filterUop, onChange, error } = props;
    let debounceTimeout = null;
    let userListLastSelectedUser = null;

    useEffect(() => {
        fetchUsers(null, MAX_INT_VALUE).then((users) => {
            setUsers(users);
            if (props.selectAll) {
                selectAllUsers();
            } else {
                deselectAllUsers();
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectAll]);

    useEffect(() => {
        fetchUsers(null, MAX_INT_VALUE, true).then((users) => {
            setUsers(users);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.contractType]);

    const fetchUsers = (q, pageSize, background) => {
        clearTimeout(debounceTimeout);

        return new Promise((resolve) => {
            debounceTimeout = setTimeout(() => {
                if (contractType) {
                    return resolve(
                        getUsersWithContracts(
                            q,
                            null,
                            null,
                            contractType,
                            DEFAULT_PAGE,
                            pageSize,
                            null,
                            null,
                            background
                        )
                    );
                }
                if (filterUop) {
                    return resolve(
                        getUsersWithContracts(
                            q,
                            null,
                            null,
                            CONTRACT.UOP,
                            DEFAULT_PAGE,
                            pageSize,
                            null,
                            null,
                            background
                        )
                    );
                }

                return resolve(getUsers(q, DEFAULT_PAGE, pageSize, null, null, background));
            }, 256);
        }).then((value) => value.content);
    };

    const sortSelectedUsers = (users) => {
        const selectedUsers = users.filter(user => userIds.includes(user.Id));
        const unselectedUsers = users.filter(user => !userIds.includes(user.Id));
        return [...selectedUsers, ...unselectedUsers];
    };

    const areAllUsersSelected = () => {
        const usersAndSelectedUsersIntersection = users.filter(
            (user) => !userIds.includes(user.Id)
        );

        return usersAndSelectedUsersIntersection.length === 0;
    };

    const isNoUserSelected = () => userIds.length === 0;

    const selectUsers = (userIds, allSelected) => onChange(userIds, allSelected);

    const deselectAllUsers = () => selectUsers([]);

    const selectAllUsers = () =>  selectUsers(users.map((user) => user.Id), true);

    const onUserSelectionChange = (e, user) => {
        const selectionIndex = userIds.indexOf(user.Id);
        const isSelected = selectionIndex >= 0;

        const event = e.nativeEvent;
        if (event.shiftKey && userListLastSelectedUser) {
            const lastSelectedUserIndex = users.indexOf(userListLastSelectedUser);
            const index = users.indexOf(user);

            const from = Math.min(lastSelectedUserIndex, index);
            const to = Math.max(lastSelectedUserIndex, index);

            let allSelected = true;
            for (let i = from; i <= to; i++) {
                if (i === lastSelectedUserIndex) {
                    // last clicked user
                    continue;
                }

                if (!userIds.includes(users[i].Id)) {
                    allSelected = false;
                    break;
                }
            }

            if (allSelected) {
                for (let i = from; i <= to; i++) {
                    const isUserSelected = userIds.includes(users[i].Id);
                    if (isUserSelected) {
                        userIds.splice(selectionIndex, 1);
                    }
                }
            } else {
                for (let i = from; i <= to; i++) {
                    userIds.push(users[i].Id);
                }
            }
        } else if (e.target.checked ^ isSelected) {
            if (e.target.checked) {
                userIds.push(user.Id);
            } else {
                userIds.splice(selectionIndex, 1);
            }
        }

        userListLastSelectedUser = user;

        const userIdsDistinct = userIds.filter((item, pos, array) => array.indexOf(item) === pos);
        selectUsers(userIdsDistinct);
    };

    return (
        <Grid container direction="column" spacing={3} className="picker-container">
            <Grid item container>
                <Grid item xs={12}>
                    <UserAutoSuggest
                        label="Users"
                        value={null}
                        fetchOptions={(q) => fetchUsers(q, DEFAULT_FETCHED_OPTIONS, true)}
                        onChange={(user) => {
                            if (!user) {
                                return;
                            }

                            const { userIds, onChange } = props;
                            const containsProject = userIds.includes(user.Id);
                            if (!containsProject) {
                                const newUserIds = [...userIds];
                                newUserIds.push(user.Id);
                                onChange(newUserIds);
                            }
                        }}
                        error={error}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormGroup className="item-list">
                        {sortSelectedUsers(users).map((user) =>
                            <FormControl key={user.Id} style={{ width: "100%" }}>
                                <FormControlLabel
                                    value={user.Name}
                                    label={user.Name}
                                    labelPlacement="end"
                                    className={
                                        userListLastSelectedUser === user
                                            ? "item--selected"
                                            : ""
                                    }
                                    control={
                                        <Checkbox
                                            onChange={(e) => onUserSelectionChange(e, user)}
                                            checked={userIds.includes(user.Id)}
                                        />
                                    }
                                />
                            </FormControl>
                        )}
                    </FormGroup>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={3} justifyContent="flex-end">
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => selectAllUsers()}
                            disabled={areAllUsersSelected()}
                        >
                            SELECT ALL
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => deselectAllUsers()}
                            disabled={isNoUserSelected()}
                        >
                            DESELECT ALL
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default UserPicker;
